import ApiClient from "src/utils/ApiClient";
import { v7 as uuidv7 } from "uuid";
import __ from "src/utils/Translations";
import { getErrorMessage } from "src/utils/constants/apiErrors";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";
import { GetAccountResponse } from "./pointsBanks.models";

const SERVICE_NAME = "points-banks";
const DEFAULT_ERROR = "Wystąpił nieoczekiwany błąd podczas operacji.";
const DEFAULT_FORBIDDEN = "Taki zasób nie istnieje lub nie masz do niego dostępu.";

export const PointsBanksApiClient = new ApiClient({
  baseURL: "/api/points-banks/api/rest",
}).client;

PointsBanksApiClient.interceptors.response.use(undefined, (err) => {
  const status = err.response?.status;

  if (status === 401) {
    return Promise.reject(err);
  }

  if (status >= 500) {
    return Promise.reject(new Error(__(DEFAULT_ERROR)));
  }

  let errorMessage;
  try {
    const errors = err.response?.data?.errors;
    if (errors?.length) {
      const mapped = getErrorMessage(SERVICE_NAME, errors[0].code, errors[0].details);
      errorMessage = mapped?.errorMessage;
    }
  } catch (e) {
    newRelicErrorReport(e, "pointsBanks/clients.ts - interceptor");
  }

  if (status === 403 || status === 404) {
    return Promise.reject(new Error(__(errorMessage || DEFAULT_FORBIDDEN)));
  }

  return Promise.reject(new Error(__(errorMessage || DEFAULT_ERROR)));
});

export const fetchAccounts = async (
  accountOwnerId: string,
  signal?: AbortSignal
): Promise<GetAccountResponse[]> => {
  const { data } = await PointsBanksApiClient.get<GetAccountResponse[]>(
    "/v1/private/accounts",
    { params: { accountOwnerId }, signal }
  );
  return data as GetAccountResponse[];
};

export const chargeAccount = (
  accountId: string,
  amount: number,
  principalId: string
): Promise<void> =>
  PointsBanksApiClient.post("/v1/private/charges", {
    externalId: uuidv7(),
    amountPerAccountId: { [accountId]: amount },
    principal: { id: principalId, type: "OMB" },
    metadata: { description: "magento.credit.omb" },
  });

export const dischargeAccount = (
  accountId: string,
  amount: number,
  principalId: string
): Promise<void> =>
  PointsBanksApiClient.post("/v1/private/discharges", {
    externalId: uuidv7(),
    amountPerAccountId: { [accountId]: amount },
    principal: { id: principalId, type: "OMB" },
    metadata: { description: "magento.debit.omb" },
  });

export const ahrChargeAccount = (accountId: string, amount: number, employeeId: string): Promise<void> =>
  PointsBanksApiClient.post(`/v1/private/account-owners/${employeeId}/ahr-charges`, {
    externalId: uuidv7(),
    amountPerAccountId: { [accountId]: amount },
  });

export const ahrDischargeAccount = (accountId: string, amount: number, employeeId: string): Promise<void> =>
  PointsBanksApiClient.post(`/v1/private/account-owners/${employeeId}/ahr-discharges`, {
    externalId: uuidv7(),
    amountPerAccountId: { [accountId]: amount },
  });
